import Fingerprint2 from 'fingerprintjs2';
import GlobalUtils from '@/lib/utils/global-utils';
import publicClient from '@/app/clients/public-client';
export default class ActivityTracker {
  private videoId: string;
  private linkId: string;
  private videoElement!: HTMLVideoElement;
  private videoElementId: string;
  private steps;
  private fingerprintPromise: Promise<string>;
  private timeMap = new Map();
  private availableStartPoints: number[];
  private fingerprint!: string;
  private started = false;
  private lastActivity: number | undefined = undefined;
  private sessionId;
  private stepTrackingEnabled = true;
  private reset = true;
  private durationAcc = 0;
  private lastEngTime: number | undefined = undefined;
  private dimension1: string | undefined = undefined;

  constructor(
    videoId: string,
    linkId: string,
    videoElementId: string,
    steps,
    dimension1?: string
  ) {
    this.videoElementId = videoElementId;
    this.videoId = videoId;
    this.linkId = linkId;
    this.fingerprintPromise = this.calculateFingerPrint();
    if (steps) {
      this.steps = steps;
      Object.keys(steps).forEach(key => {
        this.timeMap.set(steps[key], key);
      });
      this.availableStartPoints = [...this.timeMap.keys()].sort(
        (a1, b1) => a1 - b1
      );
    } else {
      this.stepTrackingEnabled = false;
      this.availableStartPoints = [];
    }

    this.sessionId = GlobalUtils.genUuid();
    this.dimension1 = dimension1;
  }

  calculateFingerPrint() {
    return new Promise<string>((resolve, reject) => {
      window.setTimeout(function() {
        Fingerprint2.get(function(components) {
          const values = components.map(function(component) {
            return component.value;
          });
          resolve(Fingerprint2.x64hash128(values.join(''), 31));
        });
      }, 500);
    });
  }

  reinitialise() {
    this.started = false;
    this.sessionId = GlobalUtils.genUuid();
  }

  start() {
    this.fingerprintPromise.then(id => {
      if (this.started) {
        return;
      }
      this.started = true;
      this.fingerprint = id;
      this.videoElement = document.getElementById(
        this.videoElementId
      ) as HTMLVideoElement;
      window.setInterval(() => this.startChecker(), 1000 / 60);
      this.videoElement.addEventListener('ended', () => {
        this.sessionId = GlobalUtils.genUuid();
        this.reset = true;
      });
      this.videoElement.addEventListener('timeupdate', () => {
        if (!this.videoElement.paused) {
          this.updateEngagement();
        }
      });
      this.videoElement.addEventListener('pause', () => {
        this.updateEngagement(true);
      });
      this.videoElement.addEventListener('play', () => {
        this.lastEngTime = Date.now();
      });
    });
  }

  get fingerPrint() {
    return this.fingerprint;
  }

  private updateEngagement(forceSync = false) {
    if (this.lastEngTime) {
      this.durationAcc += (Date.now() - this.lastEngTime) / 1000;
      if (this.durationAcc > 5 || forceSync) {
        this.sendEngagement(this.durationAcc);
        this.durationAcc = 0;
      }
    }
    this.lastEngTime = Date.now();
  }

  private startChecker() {
    if (!this.videoElement.paused) {
      if (!this.stepTrackingEnabled) {
        if (this.reset) {
          this.reset = false;
          this.sendPlayingActivity();
        }
      } else {
        const currentTime = this.videoElement.currentTime;

        const nextInterval = this.nextInterval(currentTime);
        if (nextInterval - currentTime <= 0.1) {
          this.sendActivity(currentTime);
        }
      }
    }
  }

  private sendActivity(currentTime) {
    const startTime = GlobalUtils.binarySearch(
      currentTime,
      this.availableStartPoints
    );
    const stepId = this.timeMap.get(startTime);
    if (this.lastActivity != stepId) {
      this.lastActivity = stepId;
      publicClient.trackActivity(
        this.linkId,
        this.videoId,
        stepId,
        this.fingerprint,
        this.sessionId
      );
    }
  }

  private sendEngagement(time) {
    publicClient.trackEngagement(
      this.linkId,
      this.videoId,
      this.fingerprint,
      this.sessionId,
      time,
      this.dimension1!,
      this.videoElement.currentTime
    );
  }

  private sendPlayingActivity() {
    publicClient.trackActivity(
      this.linkId,
      this.videoId,
      '999',
      this.fingerprint,
      this.sessionId
    );
  }

  private getFeedback() {
    return new Promise<string>((resolve, reject) => {
      this.fingerprintPromise.then(id => {
        publicClient
          .fetchVideoStats(this.videoId, id, this.linkId)
          .then((response: any) => {
            resolve(response);
          });
      });
    });
  }

  private nextInterval(currentTime) {
    const startTime = GlobalUtils.binarySearch(
      currentTime,
      this.availableStartPoints
    );
    const currentIndex = this.availableStartPoints.indexOf(startTime!);
    if (currentIndex + 1 < this.availableStartPoints.length) {
      return this.availableStartPoints[currentIndex + 1] as number;
    } else {
      return this.videoElement.duration;
    }
  }
}
